import { ReactNode, StateSetter, useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, Invoice, invoiceStatusConfig } from 'entities';

import {
  Column,
  Row,
  Checkbox,
  Tabs,
  TableColumn,
  Table,
  Button,
  DateRange,
  DatePicker,
} from 'components/Blocks';

import { OpenFolderIcon } from 'components/Icons';

import { useMultiStateV2 } from 'hooks';

import { SearchInvoiceSheet } from './SearchInvoiceSheet';
import { useInvoice } from '../context';

import { INVOICE_COUNTRIES } from 'utils/countries';
import { getFirst, listGet, sortByProperty, toTruthyList } from 'utils/array';
import { getInitialDateSliceParams } from 'utils/date';
import { pick } from 'utils/object';
import { safeStateUpdate } from 'utils/react';

interface InvoiceByStatusProps {
  status: string;
  leftSideActionRender?: (selected: Invoice[], update: StateSetter<Invoice[]>) => ReactNode;

  columns: TableColumn<Invoice>[];
  allowMultiSelection?: boolean;
  multiSelectionPropertiesClashes?: (keyof Invoice)[];
  addOpenButton?: boolean;
}

function getCountryTabs(invoices: Invoice[]) {
  const tabs = INVOICE_COUNTRIES.map(({ label, value }) => ({
    value,
    label,
    render: !!invoices.find(({ country }) => country === value),
  })).filter(({ render }) => render);

  return tabs;
}

const dateSliceStatuses = [
  invoiceStatusConfig.canceled.code,
  invoiceStatusConfig.invoiced.code,
  invoiceStatusConfig.paid.code,
];

function createDateId({ endDate, startDate }: { startDate: Date; endDate: Date }) {
  return [endDate, startDate].map((x) => x.toISOString()).join('');
}

export function InvoiceByStatus({
  status,
  leftSideActionRender,
  columns,
  allowMultiSelection = true,
  multiSelectionPropertiesClashes = ['country', 'client', 'currency'],
  addOpenButton = true,
}: InvoiceByStatusProps): JSX.Element {
  const { openInvoice } = useInvoice();

  const { t } = useLang();

  const hasDateSlice = dateSliceStatuses.includes(status);

  const [state, { dispatch }] = useMultiStateV2({
    tab: '',
    selected: [] as string[],
    ...getInitialDateSliceParams(),
  });

  const handleTabCalculation = useCallback(
    (invoices: Invoice[]) => {
      const newTabOptions = getCountryTabs(invoices);

      dispatch({
        tab: ({ tab }) => {
          const canKeep = !newTabOptions.length || listGet(newTabOptions, tab, 'value');

          return canKeep ? tab : getFirst(newTabOptions).value;
        },
      });
    },
    [dispatch],
  );
  const { data, overwrite, errorOrLoading } = loaders.invoice.invoicesByStatus.use({
    defaultValue: [],
    reloadOnChangeRef: [status, hasDateSlice ? createDateId(state) : ''].join(''),

    params: {
      status,
      ...pick(state, hasDateSlice ? ['endDate', 'startDate'] : []),
    },

    parser: (invoices) =>
      sortByProperty(invoices, ['clientName', 'currency', 'value'], {
        value: (a, b) => Number(b) - Number(a),
      }),

    onDataChange: (invoices) =>
      dispatch({
        tab: ({ tab }) => {
          const currentlySelected = listGet(INVOICE_COUNTRIES, tab, 'value')!;

          const exists = listGet(invoices, currentlySelected?.value, 'country');

          if (exists) return tab;

          return (
            INVOICE_COUNTRIES.find(({ value }) => listGet(invoices, value, 'country'))?.value || ''
          );
        },
      }),

    realTimeSubscription: {
      type: 'INVOICE_STATUS_UPDATE',

      onMessage: ({ invoice }, current) => {
        const updated = current.filter(({ id }) => id !== invoice.id);

        // because we are inside a set state call
        safeStateUpdate(() => handleTabCalculation(updated));

        return updated;
      },
    },
  });

  const countryTabs = getCountryTabs(data);

  const handleSelectionToggle = (invoice: Invoice): void => {
    dispatch({
      selected: ({ selected }) =>
        selected.includes(invoice.id)
          ? selected.filter((id) => id !== invoice.id)
          : [...selected, invoice.id],
    });
  };

  const handleOverwrite = (updater: Invoice[] | ((invoices: Invoice[]) => Invoice[])): void => {
    overwrite((current) => {
      const updated = typeof updater === 'function' ? updater(current) : updater;

      // because we are inside a set state call
      setInterval(() => handleTabCalculation(updated), 1);

      return updated;
    });
  };

  const invoicesByCountry = data.filter(({ country }) => country === state.tab);

  const selectedInvoices = useMemo(
    () => toTruthyList(state.selected.map((selectedId) => listGet(data, selectedId))),
    [data, state.selected],
  );

  const configWithAction = useMemo(
    () =>
      addOpenButton
        ? [
            ...columns,
            {
              className: 'w-[140px]',

              render: (invoice: Invoice) => (
                <Button variant="ghost" onClick={() => openInvoice(invoice.id)}>
                  <OpenFolderIcon />
                  {t('open')}
                </Button>
              ),
            },
          ]
        : columns,
    [columns, addOpenButton, openInvoice, t],
  );

  const changeDate = (dates?: DateRange): void => {
    dispatch((old) => ({
      ...old,
      ...(dates
        ? {
            startDate: dates.from!,
            endDate: dates.to ?? dates.from!,
          }
        : { endDate: old.startDate }),
    }));
  };

  return (
    <Column>
      <Row justifyContent="space-between">
        <Tabs tabs={countryTabs} onTabChange={(tab) => dispatch({ tab })} value={state.tab} />

        {leftSideActionRender?.(selectedInvoices, handleOverwrite)}

        {hasDateSlice && (
          <Row gap="2">
            <SearchInvoiceSheet />

            <DatePicker
              disableFuture
              selected={{ from: state.startDate, to: state.endDate }}
              onSelect={changeDate}
              mode="range"
              className="border-0"
            />
          </Row>
        )}
      </Row>

      <Table
        items={invoicesByCountry}
        loading={errorOrLoading}
        uniqueKey="id"
        spacing="medium"
        columns={
          allowMultiSelection
            ? [
                {
                  className: 'w-10',
                  dataClassName: 'pr-1',
                  render: (invoice) => {
                    const isDisabled =
                      !!invoice.cancelationRequestId ||
                      !!selectedInvoices.find((selectedInvoice) =>
                        multiSelectionPropertiesClashes.some(
                          (prop) => selectedInvoice[prop] !== invoice[prop],
                        ),
                      );

                    return (
                      <Checkbox
                        onChange={() => handleSelectionToggle(invoice)}
                        checked={!!listGet(selectedInvoices, invoice.id)}
                        disabled={isDisabled}
                      />
                    );
                  },
                },

                ...configWithAction,
              ]
            : configWithAction
        }
      />
    </Column>
  );
}
